import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { toast } from "sonner"
import { useAuth } from "@/hooks/useAuth"
import { useUpdateOrganization, useDeleteOrganization } from "@/data/hooks/api"
import { ApiError } from "@/data/api/client"
import { PageContainer } from "@/components/PageContainer"
import { PageHeader } from "@/components/PageHeader"

export function SettingsPage() {
  const { organization, checkAuth, logout } = useAuth()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [orgName, setOrgName] = useState("")
  const updateOrganization = useUpdateOrganization()
  const deleteOrganization = useDeleteOrganization()

  // Update orgName when user data loads
  useEffect(() => {
    if (organization?.name) {
      setOrgName(organization.name)
    }
  }, [organization])

  const handleSaveChanges = async () => {
    if (!orgName.trim()) {
      toast.error("Organization name cannot be empty")
      return
    }

    try {
      await updateOrganization.mutateAsync(orgName)
      toast.success("Organization settings updated successfully")
      // Refresh user data to get updated organization name
      await checkAuth()
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to update organization settings")
      }
    }
  }

  const handleDeleteOrganization = async () => {
    try {
      await deleteOrganization.mutateAsync()
      setIsDeleteDialogOpen(false)

      // Clear auth tokens
      logout()

      // Show success message
      toast.success("Organization deleted successfully. Please create a new account.")

      // Use window.location for a hard redirect to force re-authentication check
      setTimeout(() => {
        window.location.href = '/auth/register'
      }, 1500)
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to delete organization")
      }
    }
  }

  return (
    <>
      <PageContainer>
        <PageHeader
          title="Settings"
          description="Manage your organization settings and workspace preferences."
        />

        {/* Settings Group Container */}
        <div className="rounded-[8px] border border-[#EDEEF3] overflow-hidden bg-white shadow-none">
          {/* Organization Name Row */}
          <div className="flex items-center justify-between p-[16px] gap-[48px]">
            <div className="space-y-[4px] shrink-0">
              <Label htmlFor="org-name" className="text-[14px] text-[#08090A]">Organization name</Label>
              <p className="text-[13px] text-[#8A8F98]">This is your organization's visible name</p>
            </div>
            <div className="flex-1 max-w-[320px]">
              <Input
                id="org-name"
                placeholder="Enter organization name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                disabled={updateOrganization.isPending}
                className="h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[14px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus-visible:ring-0 focus-visible:border-[#654EDA] transition-colors shadow-none text-right"
              />
            </div>
          </div>

          {/* Save Button Row */}
          <div className="flex justify-end p-[13px] bg-[#F7F8F8] border-t border-[#EDEEF3]">
            <Button
              onClick={handleSaveChanges}
              disabled={updateOrganization.isPending || orgName === organization?.name}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {updateOrganization.isPending ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>

        <div className="space-y-[13px] pt-[16px]">
          <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">Workspace access</h2>
          <div className="rounded-[8px] border border-[#EDEEF3] overflow-hidden bg-white shadow-none">
            <div className="flex items-center justify-between p-[16px]">
              <div className="space-y-[2px]">
                <span className="text-[14px] text-[#08090A]">Delete organization</span>
                <p className="text-[13px] text-[#8A8F98]">Permanently delete this workspace and all its data</p>
              </div>
              <button
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={deleteOrganization.isPending}
                className="h-[32px] px-[13px] rounded-[6px] text-[14px] text-[#E74C3C] hover:bg-[#E74C3C]/10 font-medium transition-all cursor-pointer disabled:opacity-50"
              >
                Delete organization
              </button>
            </div>
          </div>
        </div>
      </PageContainer>

      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              Delete Organization
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you absolutely sure you want to delete this organization? This action cannot be undone.
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[20px]">
            <div className="space-y-[13px]">
              <p className="text-[13px] font-medium text-[#E74C3C]">
                This will permanently:
              </p>
              <ul className="list-disc pl-[20px] space-y-[8px] text-[13px] text-[#8A8F98]">
                <li>Remove all team members from the organization</li>
                <li>Delete all organization data and settings</li>
                <li>Cancel any active subscriptions</li>
                <li>Revoke all API keys and access tokens</li>
              </ul>
            </div>
            <p className="text-[13px] font-medium text-[#08090A]">
              Once deleted, there is no way to recover your organization or its data.
            </p>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteOrganization.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleDeleteOrganization}
              disabled={deleteOrganization.isPending}
              className="h-[32px] bg-[#E74C3C] hover:bg-[#E74C3C]/90 text-white font-medium"
            >
              {deleteOrganization.isPending ? "Deleting..." : "Delete Organization"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
